import { Fragment } from "react";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";

const SEGMENT_LABELS: Record<string, string> = {
  menu: "Menu",
  sections: "sidebar.menu.sections",
  collections: "sidebar.menu.collections",
  products: "sidebar.menu.products",
  promotions: "sidebar.menu.promotions",
  custom: "sidebar.menu.promotions_custom",
  section: "sidebar.menu.promotions_section",
  collection: "sidebar.menu.promotions_collection",
  "products-view": "sidebar.menu.productsView",
  settings: "sidebar.secondary.settings",
  organizations: "Organizations",
  billing: "Billing",
  pricing: "Pricing",
  admin: "Admin",
  users: "User Management",
  analytics: "sidebar.analytics",
  plans: "Plan Sync",
};

function useBreadcrumbs() {
  const { pathname } = useLocation();
  const segments = pathname.split("/").filter(Boolean);

  const items: { label: string; href?: string }[] = [
    { label: "sidebar.dashboard", href: "/dashboard" },
  ];

  let currentPath = "";
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    currentPath += `/${segment}`;

    // Skip dynamic segments like :id
    if (segment === "new" || segment === "edit") {
      items.push({ label: segment.charAt(0).toUpperCase() + segment.slice(1) });
      continue;
    }

    const label = SEGMENT_LABELS[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);

    const isLast = i === segments.length - 1;
    items.push(isLast ? { label } : { label, href: currentPath });
  }

  return items;
}

export function SiteHeader() {
  const { t } = useTranslation();
  const breadcrumbs = useBreadcrumbs();

  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator className="mx-2 data-[orientation=vertical]:h-4" orientation="vertical" />
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs.map((item, index) => (
              <Fragment key={`${item.label}-${item.href ?? "current"}`}>
                <BreadcrumbItem>
                  {item.href ? (
                    <BreadcrumbLink asChild>
                      <Link to={item.href}>{t(item.label, item.label)}</Link>
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage>{t(item.label, item.label)}</BreadcrumbPage>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </header>
  );
}
